import { useState } from 'react';
import { Navigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Button, useToast, WalletsPageSkeleton } from '@shared/ui';
import { hasPermission } from '@entities/access';
import { useAuth } from '@entities/auth';
import { mutate as globalMutate } from 'swr';
import { swrKeys } from '@shared/api/swr/keys';
import { stableWalletAddressesParams } from '@shared/api/lib/wallet-monitoring-query';
import type { WalletMonitoringAddress } from '@shared/api/contracts';
import {
  DEFAULT_WALLET_FILTERS,
  toWalletAddressQuery,
  WALLET_MONITORING_FETCH_LIMIT,
  type WalletsMonitoringFiltersPatch,
} from '@entities/wallets';
import { useWalletsMonitoring } from '@features/wallets';
import { useAddressAmlActions } from '@features/wallets';
import { useAddressControlMode } from '@features/wallets';
import { WalletsInfoBanner } from '@widgets/wallets/info-banner';
import { WalletsSummaryCards } from '@widgets/wallets/summary-cards';
import { WalletsFiltersToolbar } from '@widgets/wallets/filters-toolbar';
import { WalletGroupList } from '@widgets/wallets/wallet-group-list';
import { ControlModeModal } from '@widgets/wallets/control-mode-modal';

export function WalletsPage() {
  const { t } = useTranslation('cabinet');
  const { toast } = useToast();
  const { user, isLoading: isAuthLoading } = useAuth();
  const isAdmin = hasPermission(user, 'wallet.read');

  const [filters, setFilters] = useState(DEFAULT_WALLET_FILTERS);
  const [expandedGroups, setExpandedGroups] = useState<Set<string>>(new Set());
  const [expandedWalletIds, setExpandedWalletIds] = useState<Set<number>>(new Set());
  const [controlModeAddress, setControlModeAddress] = useState<WalletMonitoringAddress | null>(null);
  const [controlModeError, setControlModeError] = useState<string | null>(null);

  const {
    groups,
    sections,
    summary,
    filterCatalog,
    totalWallets,
    isGroupsLoading,
    isWalletsLoading,
    groupsError,
    walletsError,
    mutateGroups,
    mutateWallets,
    mutate,
  } = useWalletsMonitoring(filters);

  const { setAmlMonitoring, isPending: isAmlPending } = useAddressAmlActions();
  const { changeControlMode, isPending: isControlModePending } = useAddressControlMode();

  const handleFiltersChange = (patch: WalletsMonitoringFiltersPatch) => {
    setFilters((prev) => (typeof patch === 'function' ? patch(prev) : patch));
  };

  const handleToggleGroup = (groupCode: string) => {
    setExpandedGroups((prev) => {
      const next = new Set(prev);
      if (next.has(groupCode)) next.delete(groupCode);
      else next.add(groupCode);
      return next;
    });
  };

  const handleToggleWallet = (walletId: number) => {
    setExpandedWalletIds((prev) => {
      const next = new Set(prev);
      if (next.has(walletId)) next.delete(walletId);
      else next.add(walletId);
      return next;
    });
  };

  const revalidateAddresses = async (walletId: number) => {
    const addressQuery = stableWalletAddressesParams(
      toWalletAddressQuery(filters, 1, WALLET_MONITORING_FETCH_LIMIT, {
        networkCodes: filterCatalog.networkCodes,
        currencyCodes: filterCatalog.currencyCodes,
      }),
    );
    await globalMutate(swrKeys.wallets.addresses(walletId, addressQuery));
  };

  const handleAddressMutationSuccess = async (
    address: WalletMonitoringAddress,
    walletId: number,
  ) => {
    await revalidateAddresses(walletId);
    await mutateWallets();
    setControlModeAddress((current) =>
      current?.id === address.id ? address : current,
    );
  };

  const handleToggleAml = async (address: WalletMonitoringAddress) => {
    const result = await setAmlMonitoring(address.id, !address.aml.monitoringEnabled);

    if (result.success) {
      toast(t('common.success', { ns: 'common' }), 'success');
      await handleAddressMutationSuccess(result.data, address.walletId);
      return;
    }

    if ('notFound' in result && result.notFound) {
      toast(t('wallets.errors.addressNotFound'), 'error');
      setExpandedWalletIds((prev) => {
        const next = new Set(prev);
        next.delete(address.walletId);
        return next;
      });
      await mutateWallets();
      return;
    }

    if ('error' in result && result.error) {
      toast(result.error, 'error');
    }
  };

  const handleControlModeSubmit = async ({
    mode,
    reason,
  }: {
    mode: WalletMonitoringAddress['controlMode'];
    reason: string;
  }) => {
    if (!controlModeAddress) return;

    setControlModeError(null);
    const result = await changeControlMode(
      controlModeAddress.id,
      mode,
      controlModeAddress.controlVersion,
      reason,
    );

    if (result.success) {
      toast(t('common.success', { ns: 'common' }), 'success');
      setControlModeAddress(null);
      await handleAddressMutationSuccess(result.data, controlModeAddress.walletId);
      return;
    }

    if ('conflict' in result && result.conflict) {
      toast(t('wallets.errors.controlModeConflict'), 'error');
      await revalidateAddresses(controlModeAddress.walletId);
      await mutateWallets();
      setControlModeAddress(null);
      return;
    }

    if ('notFound' in result && result.notFound) {
      toast(t('wallets.errors.addressNotFound'), 'error');
      setControlModeAddress(null);
      setExpandedWalletIds((prev) => {
        const next = new Set(prev);
        next.delete(controlModeAddress.walletId);
        return next;
      });
      await mutateWallets();
      return;
    }

    if ('validation' in result && result.validation) {
      setControlModeError(result.validation);
      return;
    }

    if ('error' in result && result.error) {
      toast(result.error, 'error');
    }
  };

  if (isAuthLoading) {
    return <WalletsPageSkeleton />;
  }

  if (!isAdmin) return <Navigate to="/balance" replace />;

  if (groupsError) {
    return (
      <div className="page-stack">
        <div className="empty-state">
          <p className="section-description">{t('wallets.groupsLoadError')}</p>
          <Button variant="outline" onClick={() => void mutateGroups()}>
            {t('common.retry', { ns: 'common' })}
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="page-stack min-w-0">
      <WalletsInfoBanner />
      <WalletsSummaryCards
        summary={summary}
        isLoading={isWalletsLoading && !summary}
      />
      <WalletsFiltersToolbar
        groups={groups}
        filters={filters}
        catalog={filterCatalog}
        onChange={handleFiltersChange}
      />
      <WalletGroupList
        sections={sections}
        filters={filters}
        totalWallets={totalWallets}
        expandedGroups={expandedGroups}
        expandedWalletIds={expandedWalletIds}
        isLoading={isGroupsLoading || isWalletsLoading}
        error={walletsError}
        onRefresh={() => void mutate()}
        catalog={filterCatalog}
        onToggleGroup={handleToggleGroup}
        onExpandAllGroups={() => setExpandedGroups(new Set(groups.map((group) => group.code)))}
        onCollapseAllGroups={() => setExpandedGroups(new Set())}
        onToggleWallet={handleToggleWallet}
        onOpenControlMode={setControlModeAddress}
        onToggleAml={(address) => void handleToggleAml(address)}
        isAmlPending={isAmlPending}
        isControlModePending={isControlModePending}
      />

      <ControlModeModal
        open={Boolean(controlModeAddress)}
        address={controlModeAddress}
        isSubmitting={
          controlModeAddress ? isControlModePending(controlModeAddress.id) : false
        }
        serverError={controlModeError}
        onOpenChange={(open) => {
          if (!open) {
            setControlModeAddress(null);
            setControlModeError(null);
          }
        }}
        onSubmit={(payload) => void handleControlModeSubmit(payload)}
      />
    </div>
  );
}
